<template>
  <section class="card">
    <header class="head">
      <h2>人脉订单</h2>
      <p class="muted">秘书处联络 / 人脉解锁申请；会内 Tab 为目标是正式会员（ACTIVE）的记录。</p>
    </header>

    <nav class="scope-tabs" aria-label="会内会外">
      <button type="button" class="scope-tab" :class="{ active: sourceScope === 'INNER' }" @click="switchScope('INNER')">
        会内人脉
      </button>
      <button type="button" class="scope-tab" :class="{ active: sourceScope === 'OUTER' }" @click="switchScope('OUTER')">
        会外人脉
      </button>
    </nav>

    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>

    <div class="filters filters-panel">
      <div class="flt">
        <FancySelect v-model="filterStage" class="flt-select" :options="stageOptions" placeholder="阶段状态" />
      </div>
      <div class="flt">
        <FancySelect v-model="filterAudit" class="flt-select" :options="auditOptions" placeholder="审核状态" />
      </div>
      <div class="flt">
        <input
          v-model.trim="filterMobile"
          class="flt-input"
          placeholder="申请人手机号码"
          aria-label="申请人手机号码"
          @keyup.enter="load"
        />
      </div>
      <div class="flt flt--range">
        <span class="flt-range">
          <input v-model="filterCreatedFrom" class="flt-input flt-input--date" type="date" aria-label="申请时间起" />
          <span class="flt-sep">至</span>
          <input v-model="filterCreatedTo" class="flt-input flt-input--date" type="date" aria-label="申请时间止" />
        </span>
      </div>
      <div class="filter-actions">
        <button type="button" class="btn btn-primary filter-action-btn" @click="search">查询</button>
        <button type="button" class="btn btn-cancel filter-action-btn" @click="resetFilters">重置</button>
      </div>
    </div>

    <div class="table-toolbar">
      <span class="muted table-toolbar-hint">共 {{ rows.length }} 条</span>
      <button type="button" class="btn btn-edit" @click="openColumnPicker">自定义列表</button>
    </div>

    <div class="table-wrap">
    <table class="data-table">
      <thead>
        <tr>
          <th
            v-for="col in visibleColumnDefs"
            :key="col.key"
            :class="tableColClass(col)"
            :style="col.minWidth ? { minWidth: col.minWidth } : null"
          >
            {{ col.label }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, idx) in pagedRows" :key="row.id">
          <td
            v-for="col in visibleColumnDefs"
            :key="col.key + '-' + row.id"
            :class="tableColClass(col)"
          >
            <template v-if="col.key === 'seq'">{{ rowOffset + idx + 1 }}</template>
            <template v-else-if="col.key === 'stageLabel'">
              <span class="status-pill">{{ row.stageLabel || '—' }}</span>
            </template>
            <template v-else-if="col.key === 'auditStatusLabel'">
              <span class="status-pill" :class="auditPillClass(row.auditStatus)">{{ row.auditStatusLabel || '—' }}</span>
            </template>
            <template v-else-if="col.key === 'actions'">
              <button class="btn btn-edit" type="button" @click="openDetail(row)">审核</button>
            </template>
            <template v-else-if="col.ellipsis">
              <span class="ellipsis" :title="cellText(row, col.key, false)">{{ cellText(row, col.key, true) }}</span>
            </template>
            <template v-else>{{ cellText(row, col.key, false) }}</template>
          </td>
        </tr>
      </tbody>
    </table>
    </div>
    <ListPager
      v-model:page="page"
      v-model:page-size="pageSize"
      :total="rows.length"
    />
    <p v-if="!loading && !rows.length" class="muted empty-hint">暂无数据</p>

    <!-- 自定义列表（设计稿：排序 + 省略 + 显示） -->
    <div v-if="showColumnPicker" class="mask" @click.self="closeColumnPicker">
      <div class="dialog dialog--shell dialog-columns" @click.stop>
        <div class="dialog-columns-head">
          <div class="dialog-columns-title-row">
            <div class="dialog-head">
              <h3 class="dialog-title">自定义列表</h3>
              <button type="button" class="dialog-close" aria-label="关闭" @click="closeColumnPicker">×</button>
            </div>
            <button type="button" class="link-btn" @click="resetColumnPicker">还原默认设置</button>
          </div>
          <span class="dialog-columns-note">（部分特殊字段不支持控制超出省略）</span>
        </div>
        <div class="column-config-wrap">
          <table class="column-config-table">
            <thead>
              <tr>
                <th class="col-sort">排序</th>
                <th class="col-name">字段名称</th>
                <th class="col-ellipsis">超出部分省略</th>
                <th class="col-visible">是否显示</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in pickerItems" :key="item.key">
                <td class="col-sort">
                  <input
                    v-model.number="item.sort"
                    type="number"
                    min="1"
                    class="sort-input"
                    :disabled="isColumnLocked(item.key)"
                  />
                </td>
                <td class="col-name">{{ columnLabel(item.key) }}</td>
                <td class="col-ellipsis">
                  <label v-if="!isColumnNoEllipsis(item.key)" class="switch">
                    <input v-model="item.ellipsis" type="checkbox" />
                    <span class="switch-ui" />
                  </label>
                  <span v-else class="muted">—</span>
                </td>
                <td class="col-visible">
                  <label class="check-only">
                    <input
                      v-model="item.visible"
                      type="checkbox"
                      :disabled="isColumnLocked(item.key)"
                    />
                  </label>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="actions actions--center">
          <button class="btn btn-cancel" type="button" @click="closeColumnPicker">取消</button>
          <button class="btn btn-primary" type="button" @click="applyColumnPicker">确定</button>
        </div>
      </div>
    </div>

    <!-- 详情页（设计稿） -->
    <div v-if="showDialog" class="mask" @click.self="onDetailMaskClick">
      <div class="dialog dialog--shell dialog-detail" @click.stop>
        <div class="dialog-head">
          <h3 class="dialog-title">详情页</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="closeDialog">×</button>
        </div>
        <div class="dialog-body">
        <template v-if="detail">
          <p class="detail-order-no">申请单号：{{ detail.orderNo || detail.id || '—' }}</p>
          <div class="detail-title-row">
            <div class="detail-title-main">
              <h4 class="detail-title">{{ detailTitle(detail) }}</h4>
              <p class="detail-sub">{{ detail.applicantCompany || '—' }}</p>
            </div>
            <span class="detail-stage-badge">{{ detail.stageLabel || '—' }}</span>
          </div>

          <div class="detail-kv-list">
            <div class="detail-kv"><span class="k">申请人</span><span class="v">{{ applicantLine(detail) }}</span></div>
            <div class="detail-kv"><span class="k">撮合对象</span><span class="v">{{ detail.targetName || '—' }}</span></div>
            <div class="detail-kv"><span class="k">撮合对象协会</span><span class="v">{{ detail.targetCompany || '—' }}</span></div>
            <div class="detail-kv"><span class="k">撮合对象职务</span><span class="v">{{ detail.targetTitle || '—' }}</span></div>
            <div class="detail-kv"><span class="k">申请原因</span><span class="v">{{ detail.applyReason || '—' }}</span></div>
            <div v-if="detail.auditStatus === 'APPROVED'" class="detail-kv detail-kv--chat-admins">
              <span class="k">群聊管理员</span>
              <span class="v">
                <template v-if="chatAdminsList(detail).length">
                  <span v-for="(a, i) in chatAdminsList(detail)" :key="'ca-' + a.memberId">
                    {{ chatAdminLine(a) }}<template v-if="i < chatAdminsList(detail).length - 1">；</template>
                  </span>
                </template>
                <template v-else>未指定</template>
                <button
                  type="button"
                  class="btn btn-edit btn-edit-chat-admins"
                  @click="openChatAdminEditModal"
                >
                  修改群聊管理员
                </button>
              </span>
            </div>
          </div>

          <div class="flow-block">
            <h5 class="flow-title">撮合流程进展</h5>
            <div class="flow-step done">
              <div class="flow-icon flow-icon--send">✈</div>
              <div class="flow-body">
                <div class="flow-step-title">发起申请</div>
                <div class="flow-step-desc">
                  {{ detail.applicantCompany || '—' }} | {{ detail.applicantIdentityLabel || '—' }} | {{ detail.applicantName || '—' }}
                </div>
                <div class="flow-step-time">{{ detail.createdAt || '—' }}</div>
              </div>
            </div>
            <div class="flow-step" :class="{ done: reviewStepDone(detail), pending: reviewStepPending(detail) }">
              <div class="flow-icon flow-icon--user">👤</div>
              <div class="flow-body">
                <div class="flow-step-title">
                  审批人
                  <span v-if="reviewStepPending(detail)" class="flow-pending-tag">待审核</span>
                  <span v-else-if="detail.auditStatus === 'APPROVED'" class="flow-ok-tag">已通过</span>
                  <span v-else-if="detail.auditStatus === 'REJECTED'" class="flow-reject-tag">已驳回</span>
                </div>
                <div class="flow-step-desc">{{ detail.applicantCompany || '秘书处' }}</div>
                <div v-if="detail.reviewedAt" class="flow-step-time">{{ detail.reviewedAt }}</div>
                <button
                  v-if="detail.auditStatus === 'PENDING_REVIEW'"
                  class="btn btn-primary btn-approve-now"
                  type="button"
                  @click="openReviewModal"
                >
                  立即审核
                </button>
              </div>
            </div>
            <div class="flow-step" :class="{ done: detail.auditStatus === 'APPROVED' }">
              <div class="flow-icon flow-icon--done">✓</div>
              <div class="flow-body">
                <div class="flow-step-title">撮合完成</div>
              </div>
            </div>
          </div>

          <div v-if="detail.auditStatus !== 'PENDING_REVIEW'" class="detail-foot">
            <button class="btn btn-cancel" type="button" @click="closeDialog">关闭</button>
          </div>
        </template>
        </div>
      </div>

      <!-- 人脉审核（叠在详情页之上，设计稿） -->
      <div v-if="showReviewModal" class="mask mask--nested" @click.self="closeReviewModal">
        <div class="dialog dialog-review" @click.stop>
          <div class="review-modal-head">
            <h3>人脉审核</h3>
            <button type="button" class="dialog-close" aria-label="关闭" @click="closeReviewModal">×</button>
          </div>
          <div class="review-modal-body">
            <div class="review-field">
              <span class="review-label required">审核状态</span>
              <div class="radio-row">
                <label class="radio-opt">
                  <input v-model="reviewDecision" type="radio" value="approve" />
                  <span>通过</span>
                </label>
                <label class="radio-opt">
                  <input v-model="reviewDecision" type="radio" value="reject" />
                  <span>拒绝</span>
                </label>
              </div>
            </div>
            <div class="review-field">
              <span class="review-label">审核备注</span>
              <textarea
                v-model="reviewForm.reviewNote"
                class="review-textarea"
                placeholder="请输入审核备注"
                maxlength="500"
              />
            </div>
            <template v-if="reviewDecision === 'approve'">
              <div class="review-field">
                <span class="review-label">群聊管理员</span>
                <p v-if="!chatAdminOptions.length" class="review-contact-hint">
                  请先在「权限管理 → 后台用户 → 编辑」中勾选「人脉群聊管理员」。
                </p>
                <div v-else class="chat-admin-list">
                  <label v-for="opt in chatAdminOptions" :key="opt.memberId" class="chat-admin-opt">
                    <input
                      v-model="selectedChatAdminIds"
                      type="checkbox"
                      :value="opt.memberId"
                    />
                    <span>{{ opt.name || '—' }}（ID {{ opt.memberId }}<template v-if="opt.mobile"> · {{ opt.mobile }}</template>）</span>
                  </label>
                </div>
              </div>
            </template>
          </div>
          <div class="review-modal-foot">
            <button class="btn btn-cancel" type="button" :disabled="submitting" @click="closeReviewModal">取消</button>
            <button class="btn btn-primary" type="button" :disabled="submitting" @click="confirmReview">确定</button>
          </div>
        </div>
      </div>

      <!-- 已通过订单：修改群聊管理员 -->
      <div v-if="showChatAdminEditModal" class="mask mask--nested" @click.self="closeChatAdminEditModal">
        <div class="dialog dialog-review" @click.stop>
          <div class="review-modal-head">
            <h3>修改群聊管理员</h3>
            <button type="button" class="dialog-close" aria-label="关闭" @click="closeChatAdminEditModal">×</button>
          </div>
          <div class="review-modal-body">
            <p v-if="!chatAdminOptions.length" class="review-contact-hint">
              请先在「权限管理 → 后台用户 → 编辑」中勾选「人脉群聊管理员」。
            </p>
            <div v-else class="chat-admin-list">
              <label v-for="opt in chatAdminOptions" :key="'edit-' + opt.memberId" class="chat-admin-opt">
                <input
                  v-model="editChatAdminIds"
                  type="checkbox"
                  :value="opt.memberId"
                />
                <span>{{ opt.name || '—' }}（ID {{ opt.memberId }}<template v-if="opt.mobile"> · {{ opt.mobile }}</template>）</span>
              </label>
            </div>
          </div>
          <div class="review-modal-foot">
            <button class="btn btn-cancel" type="button" :disabled="submitting" @click="closeChatAdminEditModal">取消</button>
            <button class="btn btn-primary" type="button" :disabled="submitting" @click="confirmChatAdminEdit">保存</button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import FancySelect from '@/components/FancySelect.vue'
import ListPager from '@/components/ListPager.vue'
import { clampPage, pageRowOffset, slicePageRows } from '@/utils/listPager'
import {
  listNetworkConnectionRequests,
  getNetworkConnectionRequestDetail,
  reviewNetworkConnectionRequest,
  listNetworkChatAdminOptions,
  updateNetworkConnectionChatParticipants,
} from '@/api'
import {
  NETWORK_ORDER_COLUMN_DEFS,
  defaultColumnConfig,
  formatAmountCent,
  loadColumnConfig,
  saveColumnConfig,
  truncateText,
  visibleSortedColumnDefs,
} from '@/utils/networkOrderListColumns'

const STAGE_OPTIONS = [
  { value: '', label: '全部' },
  { value: 'APPOINTMENT', label: '预约' },
  { value: 'PAID', label: '已付款' },
  { value: 'SECRETARIAT_REVIEW', label: '秘书处审核' },
  { value: 'MATCHED', label: '已撮合' },
  { value: 'COMPLETED', label: '已完成' },
]

const AUDIT_OPTIONS = [
  { value: '', label: '全部' },
  { value: 'PENDING_REVIEW', label: '待审核' },
  { value: 'APPROVED', label: '已通过' },
  { value: 'REJECTED', label: '已驳回' },
]

const DEF_MAP = Object.fromEntries(NETWORK_ORDER_COLUMN_DEFS.map((c) => [c.key, c]))

export default {
  name: 'NetworkConnectionManageView',
  components: { FancySelect, ListPager },
  data() {
    return {
      sourceScope: 'INNER',
      filterStage: '',
      filterAudit: '',
      filterMobile: '',
      filterCreatedFrom: '',
      filterCreatedTo: '',
      page: 1,
      pageSize: 10,
      rows: [],
      loading: false,
      errorMsg: '',
      showDialog: false,
      showReviewModal: false,
      showChatAdminEditModal: false,
      reviewDecision: 'approve',
      detail: null,
      submitting: false,
      reviewForm: {
        reviewNote: '',
      },
      chatAdminOptions: [],
      selectedChatAdminIds: [],
      editChatAdminIds: [],
      stageOptions: STAGE_OPTIONS,
      auditOptions: AUDIT_OPTIONS,
      columnConfig: loadColumnConfig(),
      showColumnPicker: false,
      pickerItems: [],
    }
  },
  computed: {
    visibleColumnDefs() {
      return visibleSortedColumnDefs(this.columnConfig)
    },
    rowOffset() {
      return pageRowOffset(this.page, this.pageSize)
    },
    pagedRows() {
      return slicePageRows(this.rows, this.page, this.pageSize)
    },
  },
  watch: {
    'rows.length'(n) {
      this.page = clampPage(this.page, n, this.pageSize)
    },
  },
  mounted() {
    const q = this.$route && this.$route.query ? this.$route.query : {}
    const scope = String(q.sourceScope || '').trim().toUpperCase()
    if (scope === 'INNER' || scope === 'OUTER') {
      this.sourceScope = scope
    }
    const focusId = Number(q.focusId || 0)
    this.load().then(() => {
      if (!focusId) return
      const hit = (this.rows || []).find((r) => Number(r.id) === focusId)
      if (hit) this.openDetail(hit)
    })
  },
  methods: {
    tableColClass(col) {
      const key = col && col.key ? col.key : ''
      if (key === 'seq') return 'col-seq col-center'
      if (key === 'stageLabel' || key === 'auditStatusLabel') return 'col-center'
      if (key === 'actions') return 'col-actions'
      return ''
    },
    isColumnLocked(key) {
      return !!(DEF_MAP[key] && DEF_MAP[key].locked)
    },
    isColumnNoEllipsis(key) {
      return !!(DEF_MAP[key] && DEF_MAP[key].noEllipsis)
    },
    columnLabel(key) {
      return (DEF_MAP[key] && DEF_MAP[key].label) || key
    },
    openColumnPicker() {
      this.pickerItems = defaultColumnConfig().map((d) => {
        const cur = this.columnConfig.find((c) => c.key === d.key)
        return cur ? { ...cur } : { ...d }
      })
      this.pickerItems.sort((a, b) => a.sort - b.sort)
      this.showColumnPicker = true
    },
    closeColumnPicker() {
      this.showColumnPicker = false
    },
    resetColumnPicker() {
      this.pickerItems = defaultColumnConfig().map((c) => ({ ...c }))
    },
    applyColumnPicker() {
      const visibleCount = this.pickerItems.filter((c) => c.visible).length
      if (!visibleCount) {
        this.errorMsg = '请至少保留一列'
        return
      }
      this.pickerItems.forEach((it, i) => {
        it.sort = Number(it.sort) > 0 ? Number(it.sort) : i + 1
      })
      this.pickerItems.sort((a, b) => a.sort - b.sort)
      this.columnConfig = saveColumnConfig(this.pickerItems)
      this.showColumnPicker = false
    },
    cellText(row, key, forDisplay) {
      if (!row) return '—'
      let text = '—'
      switch (key) {
        case 'orderNo':
          text = row.orderNo || '—'
          break
        case 'createdAt':
          text = row.createdAt || '—'
          break
        case 'applicantInfo':
          text = this.applicantLine(row)
          break
        case 'applicantName':
          text = row.applicantName || '—'
          break
        case 'applicantMobile':
          text = row.applicantMobile || '—'
          break
        case 'applicantCompany':
          text = row.applicantCompany || '—'
          break
        case 'applicantTitle':
          text = row.applicantTitle || '—'
          break
        case 'targetName':
          text = row.targetName || '—'
          break
        case 'targetCompany':
          text = row.targetCompany || '—'
          break
        case 'targetTitle':
          text = row.targetTitle || '—'
          break
        case 'applyBusiness':
          text = row.applyBusiness || '—'
          break
        case 'applyReason':
          text = row.applyReason || '—'
          break
        case 'chatParticipants':
          text = this.chatAdminsText(row)
          break
        case 'amountCent':
          text = formatAmountCent(row.amountCent)
          break
        case 'stageLabel':
          text = row.stageLabel || '—'
          break
        case 'auditStatusLabel':
          text = row.auditStatusLabel || '—'
          break
        case 'reviewer':
          text = row.reviewer || '—'
          break
        case 'reviewedAt':
          text = row.reviewedAt || '—'
          break
        default:
          text = '—'
      }
      if (forDisplay && text && text !== '—') {
        return truncateText(text, 36)
      }
      return text
    },
    applicantLine(row) {
      const n = row && row.applicantName ? String(row.applicantName).trim() : ''
      const m = row && row.applicantMobile ? String(row.applicantMobile).trim() : ''
      if (n && m) return `${n}（${m}）`
      return n || m || '—'
    },
    chatAdminsList(row) {
      const list = row && (row.chatParticipants || row.chat_participants)
      return Array.isArray(list) ? list : []
    },
    chatAdminLine(admin) {
      if (!admin) return '—'
      const name = admin.name != null ? String(admin.name).trim() : ''
      const mobile = admin.mobile != null ? String(admin.mobile).trim() : ''
      const id = admin.memberId != null ? admin.memberId : admin.member_id
      if (name && mobile) return `${name}（${mobile}）`
      if (name) return name
      if (id) return `会员#${id}`
      return '—'
    },
    chatAdminsText(row) {
      const list = this.chatAdminsList(row)
      if (!list.length) return '—'
      return list.map((a) => this.chatAdminLine(a)).join('、')
    },
    detailTitle(detail) {
      const n = detail && detail.applicantName ? String(detail.applicantName).trim() : '申请人'
      return `${n}提交的联系秘书处申请`
    },
    reviewStepPending(detail) {
      return detail && String(detail.auditStatus || '').toUpperCase() === 'PENDING_REVIEW'
    },
    reviewStepDone(detail) {
      const c = String((detail && detail.auditStatus) || '').toUpperCase()
      return c === 'APPROVED' || c === 'REJECTED'
    },
    search() {
      this.page = 1
      this.load()
    },
    switchScope(scope) {
      if (this.sourceScope === scope) return
      this.sourceScope = scope
      this.page = 1
      this.load()
    },
    resetFilters() {
      this.filterStage = ''
      this.filterAudit = ''
      this.filterMobile = ''
      this.filterCreatedFrom = ''
      this.filterCreatedTo = ''
      this.page = 1
      this.load()
    },
    auditPillClass(code) {
      const c = String(code || '').toUpperCase()
      if (c === 'APPROVED') return 'status-pill--ok'
      if (c === 'REJECTED') return 'status-pill--reject'
      if (c === 'PENDING_REVIEW') return 'status-pill--pending'
      return ''
    },
    async load() {
      this.loading = true
      this.errorMsg = ''
      try {
        this.rows =
          (await listNetworkConnectionRequests({
            sourceScope: this.sourceScope,
            stageCode: this.filterStage || undefined,
            auditStatus: this.filterAudit || undefined,
            applicantMobile: this.filterMobile || undefined,
            createdAtFrom: this.filterCreatedFrom || undefined,
            createdAtTo: this.filterCreatedTo || undefined,
          })) || []
      } catch (e) {
        this.errorMsg = e.message || '加载失败'
        this.rows = []
      } finally {
        this.loading = false
      }
    },
    async openDetail(row) {
      try {
        this.detail = await getNetworkConnectionRequestDetail(row.id)
        this.reviewForm = { reviewNote: '' }
        this.selectedChatAdminIds = []
        this.showReviewModal = false
        this.reviewDecision = 'approve'
        this.showDialog = true
      } catch (e) {
        this.errorMsg = e.message || '加载详情失败'
      }
    },
    onDetailMaskClick() {
      if (this.showChatAdminEditModal) {
        this.closeChatAdminEditModal()
        return
      }
      if (this.showReviewModal) {
        this.closeReviewModal()
        return
      }
      this.closeDialog()
    },
    closeDialog() {
      this.showChatAdminEditModal = false
      this.showReviewModal = false
      this.showDialog = false
      this.detail = null
    },
    openReviewModal() {
      this.reviewDecision = 'approve'
      this.reviewForm.reviewNote = ''
      this.selectedChatAdminIds = []
      void this.loadChatAdminOptions()
      this.showReviewModal = true
    },
    async loadChatAdminOptions() {
      try {
        this.chatAdminOptions = (await listNetworkChatAdminOptions()) || []
      } catch (e) {
        this.chatAdminOptions = []
        this.errorMsg = e.message || '加载群聊管理员失败'
      }
    },
    closeReviewModal() {
      if (this.submitting) return
      this.showReviewModal = false
    },
    openChatAdminEditModal() {
      if (!this.detail || String(this.detail.auditStatus || '').toUpperCase() !== 'APPROVED') return
      const ids = this.chatAdminsList(this.detail).map((a) => Number(a.memberId != null ? a.memberId : a.member_id))
      this.editChatAdminIds = ids.filter((n) => Number.isFinite(n) && n > 0)
      void this.loadChatAdminOptions()
      this.showChatAdminEditModal = true
    },
    closeChatAdminEditModal() {
      if (this.submitting) return
      this.showChatAdminEditModal = false
    },
    async confirmChatAdminEdit() {
      if (!this.detail) return
      this.submitting = true
      this.errorMsg = ''
      try {
        const updated = await updateNetworkConnectionChatParticipants(this.detail.id, {
          chatParticipantMemberIds: this.editChatAdminIds
            .map((id) => Number(id))
            .filter((n) => Number.isFinite(n) && n > 0),
        })
        this.detail = updated
        this.showChatAdminEditModal = false
        await this.load()
      } catch (e) {
        this.errorMsg = e.message || '保存失败'
      } finally {
        this.submitting = false
      }
    },
    async confirmReview() {
      const approved = this.reviewDecision === 'approve'
      const note = String(this.reviewForm.reviewNote || '').trim()
      if (!approved && !note) {
        this.errorMsg = '拒绝时请填写审核备注'
        return
      }
      await this.doReview(approved)
    },
    async doReview(approved) {
      if (!this.detail) return
      this.submitting = true
      this.errorMsg = ''
      try {
        await reviewNetworkConnectionRequest(this.detail.id, {
          approved,
          reviewNote: this.reviewForm.reviewNote,
          reviewer: localStorage.getItem('admin.username') || 'admin',
          chatParticipantMemberIds: approved
            ? this.selectedChatAdminIds.map((id) => Number(id)).filter((n) => Number.isFinite(n) && n > 0)
            : [],
        })
        this.showReviewModal = false
        this.closeDialog()
        await this.load()
      } catch (e) {
        this.errorMsg = e.message || '操作失败'
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped>
.card {
  background: #fff;
  border-radius: 12px;
  padding: 18px;
}
.head h2 {
  margin: 0 0 6px;
  font-size: 20px;
}
.muted {
  color: #667085;
  font-size: 13px;
  margin: 0;
}
.scope-tabs {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 16px 0 14px;
  padding: 4px;
  border-radius: 12px;
  background: #eef2f7;
}
.scope-tab {
  border: 1px solid transparent;
  background: transparent;
  color: #475467;
  border-radius: 10px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.scope-tab:hover {
  background: rgba(255, 255, 255, 0.7);
  color: #26457d;
}
.scope-tab.active {
  background: #fff;
  color: #2f6af6;
  border-color: #d0d7e2;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
}
:deep(.filters .fs-btn) {
  height: 40px;
  min-width: 170px;
  border-radius: 8px;
}
.table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 4px 0 8px;
}
.table-toolbar-hint {
  font-size: 13px;
}
.dialog-columns {
  width: 640px;
  max-width: 94vw;
  padding: 0;
  overflow: hidden;
}
.dialog-columns-head {
  position: relative;
  padding: 16px 48px 12px 20px;
  border-bottom: 1px solid #edf0f5;
}
.dialog-columns-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dialog-columns-head h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}
.dialog-columns-note {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: #98a2b3;
}
.link-btn {
  border: none;
  background: none;
  color: #2f6af6;
  font-size: 13px;
  cursor: pointer;
  padding: 0;
}
.column-config-wrap {
  max-height: 420px;
  overflow: auto;
  padding: 0 12px;
}
.column-config-table {
  width: 100%;
  border-collapse: collapse;
}
.column-config-table th,
.column-config-table td {
  border-bottom: 1px solid #edf0f5;
  padding: 12px 10px;
  font-size: 13px;
  text-align: center;
  vertical-align: middle;
}
.column-config-table th {
  color: #667085;
  font-weight: 500;
  background: #fafbfc;
  position: sticky;
  top: 0;
  z-index: 1;
}
.column-config-table .col-name {
  text-align: left;
  min-width: 160px;
}
.sort-input {
  width: 56px;
  text-align: center;
  border: 1px solid #d0d7e2;
  border-radius: 6px;
  padding: 6px 4px;
  font-size: 13px;
}
.switch {
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}
.switch input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
.switch-ui {
  width: 36px;
  height: 20px;
  border-radius: 999px;
  background: #d0d5dd;
  position: relative;
  transition: background 0.2s;
}
.switch-ui::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.2s;
}
.switch input:checked + .switch-ui {
  background: #2f6af6;
}
.switch input:checked + .switch-ui::after {
  transform: translateX(16px);
}
.check-only input {
  width: 16px;
  height: 16px;
}
.ellipsis {
  display: inline-block;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}
.table-wrap {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
  margin-top: 8px;
  border: 1px solid #edf0f5;
  border-radius: 8px;
}
.data-table {
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
  margin: 0;
}
th,
td {
  border-bottom: 1px solid #edf0f5;
  padding: 8px 10px;
  font-size: 13px;
  text-align: left;
  vertical-align: middle;
  background: #fff;
}
.col-center,
thead .col-center,
tbody .col-center {
  text-align: center;
}
.col-seq {
  width: 56px;
  min-width: 56px;
}
.col-actions {
  position: sticky;
  right: 0;
  z-index: 2;
  min-width: 88px;
  white-space: nowrap;
  text-align: center;
  box-shadow: -6px 0 10px rgba(15, 23, 42, 0.06);
}
thead .col-actions {
  z-index: 3;
}
tbody tr:hover td {
  background: #f9fafb;
}
tbody tr:hover td.col-actions {
  background: #f9fafb;
}
.status-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  background: #f2f4f7;
  color: #344054;
}
.status-pill--ok {
  background: #ecfdf3;
  color: #027a48;
}
.status-pill--reject {
  background: #fff1f3;
  color: #c01048;
}
.status-pill--pending {
  background: #eff8ff;
  color: #175cd3;
}
.btn {
  border-radius: 8px;
  padding: 6px 12px;
  cursor: pointer;
  border: 1px solid transparent;
  font-size: 12px;
}
.btn-primary {
  background: #2f6af6;
  color: #fff;
}
.btn-edit {
  border-color: #bfc9db;
  background: #fff;
  color: #26457d;
}
.btn-delete {
  border-color: #ffc8c2;
  background: #fff1f0;
  color: #d92d20;
}
.btn-cancel {
  border-color: #d0d7e2;
  background: #fff;
  color: #344054;
}
.error {
  color: #d92d20;
  margin: 8px 0;
}
.empty-hint {
  margin-top: 12px;
}
.mask--nested {
  z-index: 1010;
  background: rgba(15, 23, 42, 0.5);
}
.dialog {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 16px 48px rgba(15, 23, 42, 0.18);
}
.dialog-detail {
  width: 520px;
  max-width: 92vw;
  max-height: 90vh;
  overflow: auto;
  padding: 0 0 20px;
}
.detail-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 8px;
  border-bottom: 1px solid #edf0f5;
}
.detail-head h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}
.dialog-close {
  border: none;
  background: none;
  font-size: 22px;
  line-height: 1;
  color: #98a2b3;
  cursor: pointer;
  padding: 0 4px;
}
.detail-order-no {
  margin: 12px 20px 0;
  font-size: 12px;
  color: #667085;
}
.detail-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 20px 0;
}
.detail-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #101828;
  line-height: 1.4;
}
.detail-sub {
  margin: 6px 0 0;
  font-size: 13px;
  color: #667085;
}
.detail-stage-badge {
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 6px;
  background: #fff6ed;
  color: #c4320a;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.detail-kv-list {
  padding: 14px 20px 0;
}
.detail-kv {
  display: flex;
  gap: 12px;
  margin-bottom: 10px;
  font-size: 13px;
  line-height: 1.5;
}
.detail-kv .k {
  flex-shrink: 0;
  width: 96px;
  color: #667085;
}
.detail-kv .v {
  flex: 1;
  color: #344054;
  word-break: break-word;
}
.btn-edit-chat-admins {
  display: inline-block;
  margin-left: 10px;
  vertical-align: middle;
  padding: 4px 10px;
  font-size: 12px;
}
.detail-kv--chat-admins .v {
  line-height: 1.6;
}
.flow-block {
  margin: 16px 20px 0;
  padding-top: 12px;
  border-top: 1px solid #edf0f5;
}
.flow-title {
  margin: 0 0 14px;
  font-size: 14px;
  font-weight: 600;
  color: #101828;
}
.flow-step {
  display: flex;
  gap: 12px;
  position: relative;
  padding-bottom: 18px;
}
.flow-step:not(:last-child)::before {
  content: '';
  position: absolute;
  left: 15px;
  top: 32px;
  bottom: 0;
  width: 2px;
  background: #e4e7ec;
}
.flow-step.done:not(:last-child)::before {
  background: #84caff;
}
.flow-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f2f4f7;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
  z-index: 1;
}
.flow-step.done .flow-icon {
  background: #eff8ff;
  color: #175cd3;
}
.flow-icon--done {
  font-size: 16px;
  font-weight: 700;
}
.flow-step-title {
  font-size: 13px;
  font-weight: 600;
  color: #101828;
  margin-bottom: 4px;
}
.flow-step-desc {
  font-size: 12px;
  color: #667085;
  line-height: 1.45;
}
.flow-step-time {
  margin-top: 4px;
  font-size: 12px;
  color: #98a2b3;
}
.flow-pending-tag {
  margin-left: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #f79009;
}
.flow-ok-tag {
  margin-left: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #027a48;
}
.flow-reject-tag {
  margin-left: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #d92d20;
}
.detail-foot {
  padding: 16px 20px 0;
  text-align: center;
}
.btn-approve-now {
  margin-top: 12px;
  min-width: 96px;
  padding: 6px 16px;
  font-size: 13px;
}
.dialog-review {
  width: 480px;
  max-width: 92vw;
  padding: 0;
  overflow: hidden;
}
.review-modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #edf0f5;
}
.review-modal-head h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}
.review-modal-body {
  padding: 20px;
}
.review-field {
  margin-bottom: 18px;
}
.review-label {
  display: block;
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #344054;
}
.review-label.required::before {
  content: '*';
  color: #f04438;
  margin-right: 4px;
}
.radio-row {
  display: flex;
  align-items: center;
  gap: 28px;
}
.radio-opt {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #344054;
  cursor: pointer;
}
.radio-opt input {
  width: 16px;
  height: 16px;
  accent-color: #2f6af6;
}
.review-textarea {
  width: 100%;
  min-height: 120px;
  box-sizing: border-box;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
}
.review-contact-hint {
  margin: 0 0 10px;
  font-size: 12px;
  color: #667085;
}
.chat-admin-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 180px;
  overflow-y: auto;
  padding: 8px 10px;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  background: #fafbfc;
}
.chat-admin-opt {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #344054;
  cursor: pointer;
}
.chat-admin-opt input {
  margin-top: 3px;
}
.review-modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px 18px;
  border-top: 1px solid #edf0f5;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 10px;
  font-size: 13px;
}
.field span {
  font-weight: 600;
  color: #344054;
}
.field input,
.field textarea {
  border: 1px solid #d0d7e2;
  border-radius: 10px;
  padding: 9px 12px;
  font-size: 13px;
}
.field textarea {
  min-height: 80px;
  resize: vertical;
}
.field--compact {
  margin-top: 8px;
}
.field--compact span {
  font-weight: 500;
  font-size: 13px;
}
.actions--center {
  text-align: center;
  border-top: 1px solid #edf0f5;
}
.actions .btn + .btn {
  margin-left: 8px;
}
</style>
